import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef} from 'react';
import groupBy from 'lodash/groupBy';
import {GroupedVirtuoso} from 'react-virtuoso';
import {
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import * as intervals from '@explorer/util/intervals';
import {ListItem} from '@explorer/components/items-panel/item-components';
import {getSelectedSeries} from '@explorer/selectors/series';
import {selectSeries, setActiveSeriesId} from '@explorer/actions/series';
import {setProcess} from '@explorer/actions/tiles.js';

const MUIComponents = {
  //   eslint-disable-next-line react/prop-types
  List: React.forwardRef(({style, children}, listRef) => {
    return (
      <List disablePadding style={style} component="div" ref={listRef}>
        {children}
      </List>
    );
  }),
  // eslint-disable-next-line react/prop-types
  Group: ({children, ...props}) => {
    return (
      <ListSubheader component="div" {...props}>
        {children}
      </ListSubheader>
    );
  },
};

const SeriesList = ({series, ...props}) => {
  const {t} = useTranslation();
  const groupedSeries = series?.length ? groupBy(series, 'interval') : {};
  const groups = Object.keys(groupedSeries);
  const virtuosoRef = useRef();

  const handleSeriesSelect = series => {
    props.selectSeries(series.id);
    props.setActiveSeriesId(series.id);
    if (!series.supports.indexes) {
      props.setProcess(null);
    }
  };

  const activeSeriesIndex = useMemo(() => {
    return series.findIndex(serie => {
      return (
        props.lastSelectedMosaics[serie.id] &&
        props.lastSelectedMosaics[serie.id] === props.selectedMosaic?.id
      );
    });
  }, [series, props.lastSelectedMosaics, props.selectedMosaic?.id]);

  useEffect(() => {
    if (activeSeriesIndex >= 0) {
      virtuosoRef.current.scrollToIndex(activeSeriesIndex);
    }
  }, [activeSeriesIndex]);

  return (
    <GroupedVirtuoso
      ref={virtuosoRef}
      components={MUIComponents}
      groupCounts={groups.map(i => groupedSeries[i]?.length)}
      groupContent={index => {
        const interval = groups[index];
        return (
          <Typography
            variant="subtitle1"
            color="textPrimary"
            data-qe={`${intervals.labels[interval]}-interval`}
          >
            {t(`basemapLabels.${intervals.labels[interval]}`)}
          </Typography>
        );
      }}
      itemContent={index => {
        return (
          <ListItem disablePadding data-qe="series-list-item">
            <ListItemButton
              selected={index === activeSeriesIndex}
              onClick={() => handleSeriesSelect(series[index])}
            >
              <ListItemText primary={series[index].name} />
              <ChevronRightIcon color="grey" />
            </ListItemButton>
          </ListItem>
        );
      }}
    />
  );
};

SeriesList.propTypes = {
  lastSelectedMosaics: PropTypes.object,
  selectSeries: PropTypes.func,
  selectedMosaic: PropTypes.object,
  selectedSeries: PropTypes.object,
  series: PropTypes.array,
  setActiveSeriesId: PropTypes.func,
  setProcess: PropTypes.func,
};

const mapStateToProps = state => ({
  lastSelectedMosaics: state.series.lastSelectedMosaics,
  selectedMosaic: state.mosaics.selected,
  selectedSeries: getSelectedSeries(state),
  seriesLoading: state.series.loading,
});

const mapDispatchProps = {
  selectSeries,
  setActiveSeriesId,
  setProcess,
};

export default connect(mapStateToProps, mapDispatchProps)(SeriesList);
